<script setup lang="ts">
import { computed, ref, type PropType } from 'vue';
import InputGroup from 'primevue/inputgroup';
import InputGroupAddon from 'primevue/inputgroupaddon';
import InputNumber from 'primevue/inputnumber';
import RadioButton from 'primevue/radiobutton';
import Select, { type SelectChangeEvent } from 'primevue/select';
import { FormFieldChoice, type FormField } from '@/models';
import type { Effect } from '../effects';
import { useI18n } from 'vue-i18n';

const { t: $t } = useI18n();

const props = defineProps({
  field: {
    type: Object as PropType<FormField>,
    required: true
  },
  previewMode: {
    type: Boolean
  }
});

const localField = ref<FormField>(props.field);

const onInputeChange = () => {
  localField.value.inputChanged = true;
};

const onInequalityChange = (event: SelectChangeEvent) => {
  if (event.value.value == 'clear') {
    localField.value.choiceValue = FormFieldChoice.createEmpty();
  }
};

const enforcedUnit = computed(
  () =>
    localField.value.options.units?.list.find(
      unitItem => unitItem.id === localField.value.options.units?.default
    ) !== undefined && localField.value.options.units.defaultFixed
);

const onUnitClick = (unitIndex: number) => {
  if (!props.previewMode && !enforcedUnit.value) {
    const currentUnit = localField.value.value;
    localField.value.value =
      localField.value.options.units!.list[unitIndex].unit;

    const parentUnit = localField.value.options.units?.list.find(unit => unit.id === localField.value.options.units?.default);
    const targetConversions: Effect[] = localField.value.options.units!.list[unitIndex].conversions as Effect[];
    
    let conversionFormula;
    for (const conversion of targetConversions) {
      const [unit, formula] = conversion;
      if (unit === parentUnit?.unit) {
        conversionFormula = formula;
      }
    }

    //converting value back to parent unit
    if (currentUnit != parentUnit?.unit && localField.value.numberValue) {
      for (const conversion of parentUnit?.conversions as Effect[]) {
        const [unit, convFormula] = conversion;
        if (unit === currentUnit) {
          const [operator, number] = convFormula;

          if (operator === '*') {
            localField.value.numberValue = localField.value.numberValue * number;
          } else if (operator === '/') {
            localField.value.numberValue = localField.value.numberValue / number;
          }
          break;
        }
      }
    }

    // convertin from parent unit to selected unit
    if (conversionFormula && localField.value.numberValue) {
      const [operator, number] = conversionFormula;
      if (operator === '*') {
        localField.value.numberValue = localField.value.numberValue * number;
      } else if (operator === '/') {
        localField.value.numberValue = localField.value.numberValue / number;
      }
    }
  }

  localField.value.inputChanged = true;
};
</script>

<template>
  <div class="item-root">
    <div class="input-with-unit">
      <InputGroup>
        <Select v-model="localField.choiceValue"
          :placeholder="$t('select')"
          optionLabel="label"
          v-if="localField.options.choices"
          :options="localField.options.choices.filter(choice => !choice.archived)"
          :disabled="previewMode"
          @change="onInequalityChange"
          class="select-input"/>
        <InputNumber id="input"
          v-model="localField.numberValue"
          :disabled="previewMode"
          :maxFractionDigits="10"
          @input="onInputeChange"/>

        <InputGroupAddon class="unit-item"
          v-for="(unitItem, index) in localField.options.units?.list"
          :key="unitItem.id"
          @click.prevent="onUnitClick(index)"
          v-ripple>
          <RadioButton v-model="localField.value"
            name="unit"
            :disabled="previewMode || enforcedUnit"
            v-if="localField.options.units!.list.length > 1"
            :value="unitItem.unit"/>
          <label for="unit" class="unit-label">
            {{ unitItem.unit }}
          </label>
        </InputGroupAddon>
      </InputGroup>
    </div>
  </div>
</template>

<style scoped lang="scss">
.p-inputgroup .p-inputtext,
.p-inputgroup-fluid .p-inputtext,
.p-inputgroup .p-inputwrapper,
.p-inputgroup-fluid .p-input,
:deep(
    .p-inputgroup .p-inputtext,
    .p-inputgroup-fluid .p-inputtext,
    .p-inputgroup .p-inputwrapper,
    .p-inputgroup-fluid .p-input
  ) {
  flex: unset;
  width: unset;
}
.unit-item {
  display: flex;
  align-items: center;
  padding: 4px 12px;

  &:hover {
    cursor: pointer;
    border-radius: 4px;
    background-color: #f5f7fa;
  }
}
.unit-label {
  display: flex;
  padding: 0 12px;
  color: #272a2e;
  font-size: 14px;
  font-weight: 500;

  &:hover {
    cursor: pointer;
  }
}
</style>
